import { useRef, useEffect, useState } from 'react';
import type { ElementPatch } from '@/components/canvas/document/publicTypes';

export interface RotateState {
  id: string;
  startRotation: number;
  startAngle: number;
  startClientX: number;
  startClientY: number;
  centerClientX: number;
  centerClientY: number;
}

export function useRotate(
  updateElementSilent: (id: string, patch: ElementPatch) => void,
  commitElement: (id: string, patch: ElementPatch) => void
) {
  const [rotateState, setRotateState] = useState<RotateState | null>(null);
  const [rotatePreview, setRotatePreview] = useState<number | null>(null);
  const rotatePreviewRef = useRef<number | null>(null);

  useEffect(() => {
    if (!rotateState) return;
    const { id, startRotation, startAngle, centerClientX, centerClientY } = rotateState;
    const onMove = (e: MouseEvent) => {
      const curAngle = Math.atan2(e.clientY - centerClientY, e.clientX - centerClientX);
      const deltaDeg = ((curAngle - startAngle) * 180) / Math.PI;
      const rotation = startRotation + deltaDeg;
      rotatePreviewRef.current = rotation;
      setRotatePreview(rotation);
      updateElementSilent(id, { layout: { rotation } });
    };
    const onUp = () => {
      // 提交最终旋转角度，生成历史记录并清除 _pendingBase
      if (rotatePreviewRef.current !== null) {
        commitElement(id, { layout: { rotation: rotatePreviewRef.current } });
      }
      setRotateState(null);
      setRotatePreview(null);
      rotatePreviewRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [rotateState, updateElementSilent, commitElement]);

  return { rotateState, setRotateState, rotatePreview, setRotatePreview, rotatePreviewRef };
}
